import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Dropdown, IconSvg } from '@/lib/v2/components';
import { DropdownItemProps } from '@/lib/v2/components/Dropdown';
import {
  DuplicateIconNew,
  EditPencilIconNew,
  MenuDotsIcon,
  MoveIconNew,
  RemoveIconNew,
} from '@/lib/v2/icons/solid';

export type ItemTypes = 'edit' | 'move' | 'duplicate' | 'delete';

interface ActionRowMenuProps {
  disabledItems?: ItemTypes[];
  hideItems?: ItemTypes[];
  isDisabled?: boolean;
  id?: string | number;
  onClick: (itemType: ItemTypes) => void;
}

const ActionRowMenu = ({
  isDisabled,
  disabledItems,
  hideItems,
  id,
  onClick,
}: ActionRowMenuProps) => {
  const { t } = useTranslation();

  const listDropdownActions: DropdownItemProps[] = useMemo(() => {
    const actions = [
      {
        id: 'edit-campaign',
        value: 'edit',
        label: t('CAMPAIGN_ACTIONS_MAIN.HEADER.DROPDOWN.edit'),
        icon: (
          <IconSvg
            fillColor="gray-dark"
            strokeColor="gray-dark"
            svgComponent={<EditPencilIconNew />}
          />
        ),
        onClick: () => onClick('edit'),
      },
      {
        id: 'move-campaign',
        value: 'move',
        label: t('CAMPAIGN_ACTIONS_MAIN.ACTIONS.tooltipMove'),
        icon: (
          <IconSvg fillColor="gray-dark" strokeColor="gray-dark" svgComponent={<MoveIconNew />} />
        ),
        onClick: () => onClick('move'),
      },
      {
        id: 'duplicate-campaign',
        value: 'duplicate',
        label: t('CAMPAIGN_ACTIONS_MAIN.ACTIONS.tooltipDuplicate'),
        icon: (
          <IconSvg
            fillColor="gray-dark"
            strokeColor="gray-dark"
            svgComponent={<DuplicateIconNew />}
          />
        ),
        onClick: () => onClick('duplicate'),
      },
      {
        id: 'delete-campaign',
        value: 'delete',
        label: t('CAMPAIGN_ACTIONS_MAIN.HEADER.DROPDOWN.delete'),
        icon: (
          <IconSvg fillColor="gray-dark" strokeColor="gray-dark" svgComponent={<RemoveIconNew />} />
        ),
        onClick: () => onClick('delete'),
      },
    ];

    const filteredActions = actions.filter(
      (action) => !hideItems?.includes(action.value as ItemTypes)
    );

    const mapActions = filteredActions.map((action) => ({
      ...action,
      disabled: disabledItems?.includes(action.value as ItemTypes),
    }));

    return mapActions;
  }, [disabledItems, hideItems, onClick, t]);

  return (
    <Dropdown
      menuOnTheLeft
      onlyIcon
      disabled={isDisabled}
      dropdownItems={listDropdownActions}
      iconRight={<IconSvg fillColor="gray-dark" svgComponent={<MenuDotsIcon />} />}
      id={id}
      label=""
    />
  );
};

export default memo(ActionRowMenu);
